import React, { useState } from 'react';

const TRANSACT_TYPES = [
  '', 'Sales Quotation', 'Sales Order', 'Delivery Notes',
  'Return Request', 'Return', 'A/R Down Payment', 'A/R Invoice', 'A/R Credit Memo',
  'Purchase Request', 'Purchase Quotation', 'Purchase Order',
  'Goods Receipt PO', 'Goods Return Request', 'Goods Return',
  'A/P Down Payment', 'A/P Invoice', 'A/P Credit Memo',
];

const emptyToRow = () => ({
  _key: Date.now() + Math.random(),
  transact_type: '', doc_number: '', ext_doc_number: '', date: '', remarks: '',
});

const emptyByRow = () => ({
  _key: Date.now() + Math.random(),
  transact_type: '', ref_document: '', reference_type: '', date: '', ref_amount: '', remarks: '',
});

const thCls = 'text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider text-sap-textLight bg-[#E8E4DC] border-b border-sap-border/40';
const tdCls = 'px-1 py-1';
const cellInp = 'w-full h-[26px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue';

export default function ReferenceInfoDialog({ refsTo = [], refsBy = [], onSave, onClose }) {
  const [tab, setTab] = useState('to');
  const [toRows, setToRows] = useState(
    refsTo.length ? refsTo.map(r => ({ ...emptyToRow(), ...r })) : [emptyToRow()]
  );
  const [byRows, setByRows] = useState(
    refsBy.length ? refsBy.map(r => ({ ...emptyByRow(), ...r })) : []
  );
  const [onlyBP, setOnlyBP] = useState(false);

  const updateTo = (idx, field, val) =>
    setToRows(rs => rs.map((r, i) => i === idx ? { ...r, [field]: val } : r));
  const updateBy = (idx, field, val) =>
    setByRows(rs => rs.map((r, i) => i === idx ? { ...r, [field]: val } : r));

  const tabCls = (t) =>
    `px-5 py-2 text-xs font-semibold border-b-2 transition-colors cursor-pointer ${
      tab === t
        ? 'text-sap-blue border-sap-blue bg-white'
        : 'text-sap-textLight border-transparent hover:text-sap-text hover:border-sap-border'
    }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl border border-sap-border w-[780px] max-h-[520px] flex flex-col"
        onClick={e => e.stopPropagation()}>

        {/* Title bar */}
        <div className="flex items-center justify-between px-4 py-2 bg-sap-blue rounded-t-xl">
          <span className="text-sap-text text-xs font-semibold">Reference Information</span>
          <button onClick={onClose} className="text-sap-textLight hover:text-sap-text text-base leading-none">✕</button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-sap-border bg-gray-50">
          <button className={tabCls('to')} onClick={() => setTab('to')}>Document Referenced To</button>
          <button className={tabCls('by')} onClick={() => setTab('by')}>Document Referenced By</button>
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-auto p-3 min-h-[280px]">
          {tab === 'to' && (
            <>
              <div className="border border-sap-border rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      <th className={`${thCls} w-7`}>#</th>
                      <th className={`${thCls} w-36`}>Transact. Type</th>
                      <th className={`${thCls} w-28`}>Doc. Number</th>
                      <th className={`${thCls} w-32`}>Ext. Doc. Number</th>
                      <th className={`${thCls} w-28`}>Date</th>
                      <th className={`${thCls}`}>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {toRows.map((r, idx) => (
                      <tr key={r._key} className="border-t border-sap-border/20 hover:bg-blue-50/30">
                        <td className={`${tdCls} text-center text-sap-textLight`}>{idx + 1}</td>
                        <td className={tdCls}>
                          <select value={r.transact_type} onChange={e => updateTo(idx, 'transact_type', e.target.value)}
                            className={`${cellInp} w-36`}>
                            {TRANSACT_TYPES.map(t => <option key={t} value={t}>{t || '—'}</option>)}
                          </select>
                        </td>
                        <td className={tdCls}>
                          <input value={r.doc_number} onChange={e => updateTo(idx, 'doc_number', e.target.value)}
                            className={cellInp} />
                        </td>
                        <td className={tdCls}>
                          <input value={r.ext_doc_number} onChange={e => updateTo(idx, 'ext_doc_number', e.target.value)}
                            className={cellInp} />
                        </td>
                        <td className={tdCls}>
                          <input type="date" value={r.date} onChange={e => updateTo(idx, 'date', e.target.value)}
                            className={cellInp} />
                        </td>
                        <td className={tdCls}>
                          <input value={r.remarks} onChange={e => updateTo(idx, 'remarks', e.target.value)}
                            className={cellInp} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 flex items-center gap-3">
                <button onClick={() => setToRows(rs => [...rs, emptyToRow()])}
                  className="text-xs text-sap-blue hover:text-sap-blueDark font-semibold">+ Add Row</button>
                <label className="flex items-center gap-1.5 text-xs text-sap-textLight ml-auto cursor-pointer">
                  <input type="checkbox" checked={onlyBP} onChange={e => setOnlyBP(e.target.checked)}
                    className="accent-sap-blue" />
                  Only Reference Business Partner on Main Document
                </label>
              </div>
            </>
          )}

          {tab === 'by' && (
            <div className="border border-sap-border rounded overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr>
                    <th className={`${thCls} w-7`}>#</th>
                    <th className={`${thCls} w-28`}>Transact. Type</th>
                    <th className={`${thCls} w-28`}>Ref. Document</th>
                    <th className={`${thCls} w-28`}>Reference Type</th>
                    <th className={`${thCls} w-24`}>Date</th>
                    <th className={`${thCls} w-24`}>Ref. Amount</th>
                    <th className={`${thCls}`}>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {byRows.length === 0 && (
                    <tr><td colSpan={7} className="px-3 py-6 text-center text-sap-textLight">No references found</td></tr>
                  )}
                  {byRows.map((r, idx) => (
                    <tr key={r._key} className="border-t border-sap-border/20 hover:bg-blue-50/30">
                      <td className={`${tdCls} text-center text-sap-textLight`}>{idx + 1}</td>
                      <td className={`${tdCls} text-sap-text`}>{r.transact_type}</td>
                      <td className={`${tdCls} text-sap-text`}>{r.ref_document}</td>
                      <td className={`${tdCls} text-sap-text`}>{r.reference_type}</td>
                      <td className={`${tdCls} text-sap-text`}>{r.date}</td>
                      <td className={`${tdCls} text-right text-sap-text`}>{r.ref_amount}</td>
                      <td className={`${tdCls} text-sap-text`}>{r.remarks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 border-t border-sap-border bg-gray-50 rounded-b-xl flex items-center gap-2">
          <button onClick={() => {
            const filtered = toRows.filter(r => r.transact_type || r.doc_number);
            onSave && onSave({ refsTo: filtered, refsBy: byRows, onlyBP });
            onClose();
          }}
            className="px-4 py-1.5 bg-sap-blue text-sap-text text-xs font-semibold rounded hover:bg-sap-blueDark transition-colors">
            OK
          </button>
          <button onClick={onClose}
            className="px-4 py-1.5 bg-gray-200 text-sap-text text-xs font-semibold rounded hover:bg-gray-300 transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
